import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../utils/api';
import api from '../../utils/api';
import { DashboardLayout } from '../../components/layout';
import { StatusBadge, Pagination, Modal, Spinner, EmptyState, Badge } from '../../components/ui';
import toast from 'react-hot-toast';

export default function AdminUsers() {
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [pg, setPg] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [group, setGroup] = useState('');
  const [search, setSearch] = useState('');
  const [pwdRequests, setPwdRequests] = useState([]);
  const [pwdCount, setPwdCount] = useState(0);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [resetModal, setResetModal] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminAPI.getUsers({ page, limit: 20, status, exam_group: group, search })
      .then(r => { setUsers(r.data.users || []); setPg(r.data.pagination || {}); })
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setLoading(false));
  }, [page, status, group, search]);

  const loadPwdRequests = useCallback(() => {
    setPwdLoading(true);
    api.get('/admin/password-reset-requests')
      .then(r => { setPwdRequests(r.data.requests || []); setPwdCount(r.data.pendingCount || 0); })
      .catch(() => {})
      .finally(() => setPwdLoading(false));
  }, []);

  useEffect(() => { load(); loadPwdRequests(); }, [load, loadPwdRequests]);

  const doAction = async (fn, msg) => {
    setActionLoading(true);
    try { await fn(); toast.success(msg); load(); loadPwdRequests(); }
    catch (e) { toast.error(e.response?.data?.error || 'Action failed'); }
    setActionLoading(false);
  };

  const openView = u => {
    adminAPI.getUserDetails(u.id)
      .then(r => { setSelected(r.data); setViewModal(true); })
      .catch(() => toast.error('Failed'));
  };
  const openEdit = u => {
    setEditForm({ full_name: u.full_name, email: u.email, phone: u.phone, exam_group: u.exam_group, category: u.category || '', gender: u.gender || '' });
    setSelected({ user: u });
    setEditModal(true);
  };
  const openReset = u => { setResetUser(u); setNewPassword(''); setResetModal(true); };

  const handleApprove = u => doAction(() => api.post(`/admin/users/${u.id}/approve`), '✅ User approved!');
  const handleReject  = u => doAction(() => api.post(`/admin/users/${u.id}/reject`), '❌ User rejected');
  const handleBlock   = u => doAction(() => api.post(`/admin/users/${u.id}/block`), u.status === 'suspended' ? '✅ Unblocked' : '🚫 Blocked');
  const handleDelete  = u => {
    if (!window.confirm(`Delete ${u.full_name}? Cannot be undone.`)) return;
    doAction(() => api.delete(`/admin/users/${u.id}`), '🗑️ User deleted');
  };
  const handleEditSave = () => doAction(async () => {
    await api.put(`/admin/users/${selected.user.id}/edit`, editForm);
    setEditModal(false);
  }, '✏️ User updated!');
  const handleResetSave = () => doAction(async () => {
    if (!newPassword || newPassword.length < 6) throw { response: { data: { error: 'Min 6 characters' } } };
    await api.post(`/admin/users/${resetUser.id}/reset-password`, { new_password: newPassword });
    setResetModal(false);
  }, '🔐 Password reset!');
  const handlePwdResolve = req => doAction(() => api.put(`/admin/password-reset-requests/${req.id}/resolve`), '✅ Resolved');
  const handleResetProgress = u => {
    if (!window.confirm(`Reset ALL batch progress for ${u.full_name}? They will restart from Batch 1.`)) return;
    doAction(() => api.post(`/admin/users/${u.id}/reset-progress`), '🔄 Progress reset to Batch 1!');
  };
  const handleUnlockBatch = async (u) => {
    const bno = prompt(`Unlock which batch number for ${u.full_name}? (1-100)`);
    if (!bno || isNaN(parseInt(bno))) return;
    doAction(() => api.post(`/admin/users/${u.id}/unlock-batch/${bno}`), `🔓 Batch ${bno} unlocked!`);
  };

  const labelStyle = { fontSize: 11, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 };

  return (
    <DashboardLayout title="User Management" subtitle={`${pg.total || 0} total users`} isAdmin>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
        {[['users','👥 Users'], ['pwdRequests', `🔐 Pwd Requests${pwdCount > 0 ? ` (${pwdCount})` : ''}`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '9px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
            fontWeight: 700, fontSize: 13, fontFamily: 'var(--font-body)', position: 'relative',
            background: tab === k ? 'var(--blue)' : 'var(--bg2)',
            color: tab === k ? '#fff' : 'var(--muted)',
          }}>
            {l}
            {k === 'pwdRequests' && pwdCount > 0 && (
              <span style={{ position:'absolute', top:-5, right:-5, background:'#e53935', color:'#fff', borderRadius:'50%', width:18, height:18, fontSize:10, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800 }}>{pwdCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── USERS TAB ── */}
      {tab === 'users' && (<>
        <div style={{ display:'flex', gap:10, marginBottom:18, flexWrap:'wrap' }}>
          <input placeholder="🔍 Search name, email, phone..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ flex:1, minWidth:180, padding:'9px 14px', border:'1px solid var(--border)', borderRadius:9, fontSize:13, fontFamily:'var(--font-body)' }} />
          <select className="form-select no-icon" style={{ width:140 }} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All Status</option>
            <option value="active">✅ Active</option>
            <option value="pending">⏳ Pending</option>
            <option value="rejected">❌ Rejected</option>
            <option value="suspended">🚫 Suspended</option>
          </select>
          <select className="form-select no-icon" style={{ width:140 }} value={group} onChange={e => { setGroup(e.target.value); setPage(1); }}>
            <option value="">All Groups</option>
            <option value="Group1">Group 1</option><option value="Group2">Group 2</option>
            <option value="Group2A">Group 2A</option><option value="Group4">Group 4</option>
          </select>
          <button className="btn btn-outline btn-sm" onClick={load}>🔄</button>
        </div>

        <div className="card">
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr><th>#</th><th>Student</th><th>Phone</th><th>Group</th><th>Registered</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {loading
                  ? <tr><td colSpan={7} style={{ textAlign:'center', padding:40 }}><Spinner size={30} color="blue" /></td></tr>
                  : users.length === 0
                    ? <tr><td colSpan={7}><EmptyState icon="👥" title="No users found" /></td></tr>
                    : users.map((u, i) => (
                      <tr key={u.id}>
                        <td style={{ fontFamily:'var(--font-mono)', color:'var(--muted)', fontSize:11 }}>{(page-1)*20+i+1}</td>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                            <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg,var(--blue),var(--blue-mid))', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:12, fontWeight:700, flexShrink:0 }}>{u.full_name[0]}</div>
                            <div>
                              <div style={{ fontWeight:600, fontSize:13 }}>{u.full_name}</div>
                              <div style={{ fontSize:11, color:'var(--muted)' }}>{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <a href={`https://wa.me/91${u.phone}`} target="_blank" rel="noreferrer"
                            style={{ color:'#25D366', fontWeight:600, textDecoration:'none', fontSize:12, fontFamily:'var(--font-mono)' }}>
                            📲 {u.phone}
                          </a>
                        </td>
                        <td><Badge type="blue">{u.exam_group}</Badge></td>
                        <td style={{ fontSize:12, color:'var(--muted)' }}>{new Date(u.created_at).toLocaleDateString('en-IN')}</td>
                        <td><StatusBadge status={u.status} /></td>
                        <td>
                          <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                            {u.status !== 'active'    && <button className="btn btn-success btn-sm" onClick={()=>handleApprove(u)} title="Approve">✅ Approve</button>}
                            {u.status !== 'rejected'  && <button className="btn btn-danger btn-sm"  onClick={()=>handleReject(u)}  title="Reject">❌ Reject</button>}
                            <button className="btn btn-outline btn-sm" onClick={()=>openEdit(u)}    title="Edit">✏️ Edit</button>
                            <button className="btn btn-outline btn-sm" onClick={()=>openReset(u)}   title="Reset Password" style={{ color:'var(--blue)' }}>🔐 Reset</button>
                            <button className="btn btn-outline btn-sm" onClick={()=>handleBlock(u)} title={u.status==='suspended'?'Unblock':'Block'} style={{ color: u.status==='suspended'?'var(--success)':'#e65100' }}>
                              {u.status === 'suspended' ? '✅ Unblock' : '🚫 Block'}
                            </button>
                            <button className="btn btn-danger btn-sm"  onClick={()=>handleDelete(u)} title="Delete">🗑️</button>
                            <button className="btn btn-outline btn-sm" onClick={()=>openView(u)}    title="View Details">👁️</button>
                          </div>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        </div>
        <Pagination page={page} pages={pg.pages || 1} onPage={setPage} />
      </>)}

      {/* ── PASSWORD REQUESTS TAB ── */}
      {tab === 'pwdRequests' && (<>
        <p style={{ color:'var(--muted)', fontSize:13, marginBottom:16 }}>Users who requested a password change. Reset their password and notify via WhatsApp.</p>
        {pwdLoading
          ? <div style={{ textAlign:'center', padding:60 }}><Spinner size={30} color="blue" /></div>
          : pwdRequests.length === 0
            ? <EmptyState icon="🎉" title="No pending password requests!" subtitle="All clear." />
            : pwdRequests.map(req => (
              <div key={req.id} className="card" style={{ marginBottom:12, padding:'18px 20px', display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
                <div style={{ flex:1, minWidth:200 }}>
                  <div style={{ fontWeight:700, color:'var(--blue)', fontSize:15 }}>👤 {req.full_name}</div>
                  <div style={{ color:'var(--muted)', fontSize:13, marginTop:4 }}>📞 {req.phone} &nbsp;|&nbsp; ✉️ {req.email}</div>
                  <div style={{ color:'var(--muted)', fontSize:11, marginTop:4 }}>
                    Requested: {new Date(req.created_at).toLocaleString('en-IN')} | Group: {req.exam_group}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <a href={`https://wa.me/91${req.phone}?text=${encodeURIComponent(`Hi ${req.full_name}, your TNPSC Hub password has been reset. Please login with your new password at https://tnpschub.msdiscoveries.com/login`)}`}
                    target="_blank" rel="noreferrer" className="btn btn-success btn-sm">📲 Notify WhatsApp</a>
                  <button className="btn btn-primary btn-sm" onClick={()=>openReset({ id:req.user_id, full_name:req.full_name, phone:req.phone })}>🔐 Reset Password</button>
                  <button className="btn btn-outline btn-sm" onClick={()=>handlePwdResolve(req)}>✅ Resolve</button>
                </div>
              </div>
            ))}
      </>)}

      {/* VIEW MODAL */}
      <Modal open={viewModal} onClose={()=>setViewModal(false)} title="User Details" size={580}
        footer={<>
          <button className="btn btn-outline" onClick={()=>setViewModal(false)}>Close</button>
          {selected?.user?.status !== 'active'   && <button className="btn btn-success" onClick={()=>{handleApprove(selected.user);setViewModal(false);}}>✅ Approve</button>}
          {selected?.user?.status !== 'rejected' && <button className="btn btn-danger btn-outline" onClick={()=>{handleReject(selected.user);setViewModal(false);}}>❌ Reject</button>}
          {selected?.user?.status === 'active'   && <button className="btn btn-danger" onClick={()=>{handleBlock(selected.user);setViewModal(false);}}>🚫 Block</button>}
          <button className="btn btn-primary" onClick={()=>{setViewModal(false);openReset(selected?.user);}}>🔐 Reset Pwd</button>
        </>}>
        {selected && (
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:22 }}>
              <div style={{ width:56, height:56, borderRadius:'50%', background:'linear-gradient(135deg,var(--blue),var(--blue-mid))', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:22, fontWeight:700 }}>{selected.user?.full_name?.[0]}</div>
              <div>
                <h3 style={{ fontFamily:'var(--font-heading)', fontSize:19, margin:0 }}>{selected.user?.full_name}</h3>
                <div style={{ color:'var(--muted)', fontSize:13 }}>{selected.user?.email}</div>
                <div style={{ marginTop:6 }}><StatusBadge status={selected.user?.status} /></div>
              </div>
            </div>
            {[['Phone',selected.user?.phone],['Exam Group',selected.user?.exam_group],['Category',selected.user?.category],['Gender',selected.user?.gender],['DOB',selected.user?.date_of_birth?.split('T')[0]],['Address',selected.user?.address]].map(([l,v])=>v&&(
              <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'9px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                <span style={{ color:'var(--muted)', fontWeight:600 }}>{l}</span>
                <span style={{ maxWidth:'60%', textAlign:'right' }}>{v}</span>
              </div>
            ))}
            {selected.payment && (
              <div style={{ background:'var(--bg)', borderRadius:10, padding:14, marginTop:14 }}>
                <div style={{ fontWeight:700, marginBottom:7, fontSize:13 }}>💰 Payment</div>
                <div style={{ fontSize:13 }}>UTR: <strong style={{ fontFamily:'var(--font-mono)' }}>{selected.payment.utr_id}</strong> &nbsp;|&nbsp; ₹{selected.payment.amount} &nbsp;|&nbsp; <StatusBadge status={selected.payment.status} /></div>
              </div>
            )}
            {selected.testResults?.length > 0 && (
              <div style={{ marginTop:14 }}>
                <div style={{ fontWeight:700, fontSize:13, marginBottom:8 }}>📊 Recent Tests</div>
                {selected.testResults.slice(0,5).map(r=>(
                  <div key={r.id} style={{ display:'flex', justifyContent:'space-between', background:'var(--bg)', borderRadius:8, padding:'8px 12px', marginBottom:6, fontSize:13 }}>
                    <span>{r.batch_title||r.test_title||`Batch ${r.batch_number}`}</span>
                    <strong style={{ color: parseFloat(r.percentage)>=60?'#28A745':'#dc3545' }}>{parseFloat(r.percentage||0).toFixed(1)}%</strong>
                  </div>
                ))}
                <div style={{ marginTop:14, display:'flex', gap:8 }}>
                  <button onClick={()=>handleUnlockBatch(selected.user)}
                    style={{ flex:1, padding:'7px 10px', borderRadius:8, border:'1px solid var(--blue)', background:'var(--bg)', color:'var(--blue)', cursor:'pointer', fontSize:12, fontWeight:700 }}>
                    🔓 Unlock Batch
                  </button>
                  <button onClick={()=>handleResetProgress(selected.user)}
                    style={{ flex:1, padding:'7px 10px', borderRadius:8, border:'1px solid #dc3545', background:'var(--bg)', color:'#dc3545', cursor:'pointer', fontSize:12, fontWeight:700 }}>
                    🔄 Reset Progress
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* EDIT MODAL */}
      <Modal open={editModal} onClose={()=>setEditModal(false)} title={`✏️ Edit — ${selected?.user?.full_name}`} size={500}
        footer={<>
          <button className="btn btn-outline" onClick={()=>setEditModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleEditSave} disabled={actionLoading}>
            {actionLoading ? <Spinner size={16} color="white" /> : '💾 Save Changes'}
          </button>
        </>}>
        <div className="g2">
          {[['full_name','Full Name','👤'],['email','Email','📧'],['phone','Phone','📞']].map(([k,l,ic])=>(
            <div key={k} className="form-group">
              <label style={labelStyle}>{l}</label>
              <div className="input-wrap"><span className="input-icon">{ic}</span>
                <input className="form-input" value={editForm[k]||''} onChange={e=>setEditForm(p=>({...p,[k]:e.target.value}))} />
              </div>
            </div>
          ))}
          <div className="form-group">
            <label style={labelStyle}>Exam Group</label>
            <select className="form-select" value={editForm.exam_group||''} onChange={e=>setEditForm(p=>({...p,exam_group:e.target.value}))}>
              {['Group1','Group2','Group2A','Group4','Common'].map(g=><option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label style={labelStyle}>Gender</label>
            <select className="form-select" value={editForm.gender||''} onChange={e=>setEditForm(p=>({...p,gender:e.target.value}))}>
              <option value="">Select</option>
              <option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option>
            </select>
          </div>
        </div>
      </Modal>

      {/* RESET PASSWORD MODAL */}
      <Modal open={resetModal} onClose={()=>setResetModal(false)} title={`🔐 Reset Password — ${resetUser?.full_name}`} size={440}
        footer={<>
          <button className="btn btn-outline" onClick={()=>setResetModal(false)}>Cancel</button>
          {resetUser?.phone && newPassword && (
            <a href={`https://wa.me/91${resetUser.phone}?text=${encodeURIComponent(`Hi ${resetUser?.full_name}, your TNPSC Hub password has been reset to: ${newPassword}\n\nLogin: https://tnpschub.msdiscoveries.com/login`)}`}
              target="_blank" rel="noreferrer" className="btn btn-success">📲 Send on WhatsApp</a>
          )}
          <button className="btn btn-primary" onClick={handleResetSave} disabled={actionLoading}>
            {actionLoading ? <Spinner size={16} color="white" /> : '✅ Confirm Reset'}
          </button>
        </>}>
        <p style={{ color:'var(--muted)', fontSize:13, margin:'0 0 16px' }}>
          Set a new password for <strong>{resetUser?.full_name}</strong>. An in-app notification will be sent automatically.
        </p>
        <div className="form-group">
          <label style={labelStyle}>New Password (min 6 characters)</label>
          <div className="input-wrap"><span className="input-icon">🔒</span>
            <input type="text" className="form-input" placeholder="Enter new password..." value={newPassword} onChange={e=>setNewPassword(e.target.value)} />
          </div>
        </div>
        {resetUser?.phone && (
          <div style={{ background:'rgba(37,211,102,0.08)', border:'1px solid rgba(37,211,102,0.25)', borderRadius:10, padding:'11px 14px', fontSize:13, color:'#1a7a4a' }}>
            💡 After resetting, use <strong>"Send on WhatsApp"</strong> to notify +91 {resetUser.phone}
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
}
